import { useRef, useState } from "react";
import SvgImageCarousel from "../../../components/svgImageCarousel/SvgImageCarousel";
import "./PainAssessmentViewer.css";

import { ReactComponent as FemaleAnterior } from "../../../assets/images/painAssesment/female_anterior.svg";
import { ReactComponent as FemaleLeft } from "../../../assets/images/painAssesment/female_left.svg";
import { ReactComponent as FemalePosterior } from "../../../assets/images/painAssesment/female_posterior.svg";
import { ReactComponent as FemaleRight } from "../../../assets/images/painAssesment/female_right.svg";
import { ReactComponent as MaleAnterior } from "../../../assets/images/painAssesment/male_anterior.svg";
import { ReactComponent as MaleLeft } from "../../../assets/images/painAssesment/male_left.svg";
import { ReactComponent as MalePosterior } from "../../../assets/images/painAssesment/male_posterior.svg";
import { ReactComponent as MaleRight } from "../../../assets/images/painAssesment/male_right.svg";

const PainAssessmentViewer = ({ assessment, className = "" }) => {
  const drawingAreaRef = useRef(null);
  const [currentOrientation, setCurrentOrientation] = useState(0);
  const [selectedPathIndex, setSelectedPathIndex] = useState(null);
  const [hoveredPathInfo, setHoveredPathInfo] = useState(null);

  // Get SVG components based on gender - SAME AS CAROUSEL
  const getSvgComponents = () => {
    const components = {
      Male: [
        { component: MaleAnterior },
        { component: MaleLeft },
        { component: MalePosterior },
        { component: MaleRight },
      ],
      Female: [
        { component: FemaleAnterior },
        { component: FemaleLeft },
        { component: FemalePosterior },
        { component: FemaleRight },
      ],
    };
    return components[assessment?.gender] || components.Male;
  };

  // Handle orientation change
  const handleOrientationChange = newIndex => {
    setCurrentOrientation(newIndex);
    setSelectedPathIndex(null);
    setHoveredPathInfo(null);
  };

  // Handle path click
  const handlePathClick = (pathIndex, event) => {
    event.preventDefault();
    event.stopPropagation();
    setSelectedPathIndex(pathIndex);
  };

  const handlePathHover = (pathObj, event) => {
    if (!pathObj || !pathObj.painLevel) return;

    const containerRect = drawingAreaRef.current?.getBoundingClientRect();
    const offsetX = (event?.clientX ?? 0) - (containerRect?.left ?? 0);
    const offsetY = (event?.clientY ?? 0) - (containerRect?.top ?? 0);

    const hoverPosition = containerRect
      ? { x: Math.max(0, offsetX), y: Math.max(0, offsetY) }
      : { x: 0, y: 0 };

    setHoveredPathInfo({
      painLevel: pathObj.painLevel,
      painType: pathObj.painType,
      position: hoverPosition,
    });
  };

  const handlePathLeave = () => {
    setHoveredPathInfo(null);
  };

  if (!assessment) {
    return (
      <div className={`pain-assessment-viewer ${className}`}>
        <div className="pain-assessment-viewer-error">
          No pain assessment data available
        </div>
      </div>
    );
  }

  const svgComponents = getSvgComponents();

  // Render using same carousel logic with hover overlay
  const renderViewingArea = () => {
    return (
      <div className="pain-drawing-container">
        <div
          className="pain-scalable-drawing-wrapper"
          ref={drawingAreaRef}
          onMouseLeave={handlePathLeave}
        >
          <SvgImageCarousel
            svgComponents={svgComponents}
            activeIndex={currentOrientation}
            onSvgClick={handleOrientationChange}
            isDisabled={false}
            allPaths={assessment.allPaths}
            selectedPathIndex={selectedPathIndex}
            onPathClick={handlePathClick}
            onPathHover={handlePathHover}
            onPathLeave={handlePathLeave}
            canDraw={false}
          />
          {hoveredPathInfo && (
            <div
              className="pain-path-tooltip"
              style={{
                left: hoveredPathInfo.position?.x || 0,
                top: hoveredPathInfo.position?.y || 0,
              }}
            >
              <div className="pain-path-tooltip-level">
                Pain level {hoveredPathInfo.painLevel}
              </div>
              <div className="pain-path-tooltip-type">
                Type: {hoveredPathInfo.painType || "Not specified"}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className={`pain-assessment-viewer ${className}`}>
      <div className="pain-assessment-viewer-container">
        {renderViewingArea()}
      </div>
    </div>
  );
};

export default PainAssessmentViewer;
